import { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, Power } from 'lucide-react';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';
import { Reorder, useDragControls } from 'framer-motion';
import { useAuth } from '@/shared/lib/auth';
import { canAccessTenantPath } from '@/shared/lib/navigation';
import { usePassbook } from '@/shared/lib/passbook';
import { PAGE_HELP } from '@/shared/config/page-help';
import { useSelection } from '@/shared/lib/use-selection';
import { useConfirm } from '@/shared/components/confirm';
import { PageHeader } from '@/shared/components/page-header';
import { FloatingActionBar } from '@/shared/components/floating-action-bar';
import { SelectableListLayout } from '@/shared/components/selectable-list-layout';
import { Button } from '@/shared/ui/button';
import { Tooltip } from '@/shared/ui/tooltip';
import { Pagination } from '@/shared/ui/pagination';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import type { Banner } from '@/shared/types/banner';
import { isPersistedListId, normalizeOrderedListItems } from '@/shared/lib/ordered-list-items';
import {
  useBannersFetcher,
  toggleBannerActive,
  reorderBanner,
  deleteBanner,
} from '../api/use-banners';
import { resolveBannerSrc } from '../model/banner-image';
import { BannerCreateDialog } from './banner-create-dialog';
import { BannerEditDialog } from './banner-edit-dialog';
import { BannerCard } from './banner-card';

const PAGE_SIZE = 25;

interface DraggableBannerItemProps {
  banner: Banner;
  index: number;
  selected: boolean;
  onToggle: () => void;
  onPreview: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: () => void;
  onDragEnd: () => void;
  canPersist: boolean;
}

function DraggableBannerItem({
  banner,
  index,
  selected,
  onToggle,
  onPreview,
  onEdit,
  onDelete,
  onToggleActive,
  onDragEnd,
  canPersist,
}: DraggableBannerItemProps) {
  const dragControls = useDragControls();
  return (
    <Reorder.Item
      value={banner}
      dragListener={false}
      dragControls={dragControls}
      onDragEnd={onDragEnd}
      className="list-none"
      drag={canPersist}
    >
      <BannerCard
        banner={banner}
        index={index}
        selected={selected}
        onToggle={onToggle}
        onPreview={onPreview}
        onEdit={onEdit}
        onDelete={onDelete}
        onToggleActive={onToggleActive}
        canPersist={canPersist}
        onDragHandlePointerDown={canPersist ? (e) => dragControls.start(e) : undefined}
      />
    </Reorder.Item>
  );
}

export function BannersPage() {
  const { user } = useAuth();
  const { idPassbook, passbook } = usePassbook();
  const fetchData = useBannersFetcher(idPassbook);
  const confirm = useConfirm();

  const [rows, setRows] = useState<Banner[]>([]);
  const [total, setTotal] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [listKey, setListKey] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [editBanner, setEditBanner] = useState<Banner | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [batchBusy, setBatchBusy] = useState(false);

  const persistedOrderRef = useRef<string[]>([]);
  const selection = useSelection(rows);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPageIndex(0);
    selection.clear();
  }, [debouncedSearch, idPassbook]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    fetchData({
      pageIndex,
      pageSize: PAGE_SIZE,
      sorting: [{ id: 'order', desc: false }],
      search: debouncedSearch,
    })
      .then((result) => {
        if (!cancelled) {
          const normalized = normalizeOrderedListItems(result.rows, (b, i) =>
            `${(b.banner ?? '').slice(-24)}:${(b.title ?? '').slice(0, 24)}:${i}`,
          );
          setRows(normalized);
          setTotal(result.total);
          persistedOrderRef.current = normalized.map((b) => b._id);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pageIndex, debouncedSearch, fetchData, idPassbook, listKey]);

  const refresh = useCallback(() => {
    setListKey((k) => k + 1);
    selection.clear();
  }, [selection]);

  const persistDragEnd = useCallback(
    async (bannerId: string) => {
      if (!isPersistedListId(bannerId)) return;
      const before = persistedOrderRef.current;
      const from = before.indexOf(bannerId);
      const to = rows.findIndex((b) => b._id === bannerId);
      if (from === -1 || to === -1 || from === to) return;

      persistedOrderRef.current = rows.map((b) => b._id);
      try {
        await reorderBanner(idPassbook, bannerId, from, to);
        refresh();
      } catch {
        toast.error('Falha ao reordenar banner.');
        refresh();
      }
    },
    [rows, idPassbook, refresh],
  );

  const handleToggle = useCallback(
    async (banner: Banner) => {
      if (!isPersistedListId(banner._id)) {
        toast.error('Este banner não tem id no banco. Reordene um item válido para sincronizar a lista.');
        return;
      }
      try {
        await toggleBannerActive(idPassbook, banner._id, !banner.active);
        toast.success(banner.active ? 'Banner desativado.' : 'Banner ativado.');
        refresh();
      } catch {
        toast.error('Falha ao atualizar status.');
      }
    },
    [idPassbook, refresh],
  );

  const handleDelete = useCallback(
    async (banner: Banner) => {
      if (!isPersistedListId(banner._id)) {
        toast.error('Este banner não tem id no banco e não pode ser excluído por aqui.');
        return;
      }
      const ok = await confirm({
        title: 'Excluir banner',
        description: `Remover permanentemente "${banner.title || 'este banner'}"?`,
        confirmLabel: 'Excluir',
        variant: 'destructive',
      });
      if (!ok) return;
      try {
        await deleteBanner(idPassbook, banner._id);
        toast.success('Banner excluído.');
        refresh();
      } catch {
        toast.error('Falha ao excluir banner.');
      }
    },
    [confirm, idPassbook, refresh],
  );

  const batchSetActive = useCallback(
    async (active: boolean) => {
      if (selection.selected.length === 0) return;
      setBatchBusy(true);
      let ok = 0;
      let fail = 0;
      let skipped = 0;
      for (const banner of selection.selected) {
        if (!isPersistedListId(banner._id)) {
          skipped += 1;
          continue;
        }
        if (Boolean(banner.active) === active) continue;
        try {
          await toggleBannerActive(idPassbook, banner._id, active);
          ok += 1;
        } catch {
          fail += 1;
        }
      }
      setBatchBusy(false);
      if (ok > 0) {
        toast.success(
          `${ok} banner${ok > 1 ? 's' : ''} ${active ? 'ativado' : 'desativado'}${ok > 1 ? 's' : ''}.`,
        );
      }
      if (skipped > 0) {
        toast.message(`${skipped} item(ns) sem id ignorado(s). Reordene a lista para sincronizar.`);
      }
      if (fail > 0) toast.error(`${fail} falha${fail > 1 ? 's' : ''}.`);
      if (ok > 0) refresh();
    },
    [selection.selected, idPassbook, refresh],
  );

  if (
    user &&
    !canAccessTenantPath(user.role, `/loja/${idPassbook}/banners`, passbook, user.email)
  ) {
    return <Navigate to={`/loja/${idPassbook}`} replace />;
  }

  const showSkeleton = isLoading && rows.length === 0;

  return (
    <>
      <PageHeader
        title="Banners"
        hideMetaOnMobile
        help={PAGE_HELP.banners}
        description={`Gerenciar banners — ${passbook.organizationName}`}
        actions={
          <Tooltip content="Abrir assistente para enviar nova imagem promocional" side="bottom">
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" /> Adicionar banner
            </Button>
          </Tooltip>
        }
      />

      <SelectableListLayout
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por título ou descrição…"
        listWrapper={(children) => (
          <Reorder.Group
            as="ul"
            axis="y"
            values={rows}
            onReorder={setRows}
            className="space-y-2.5 p-0"
          >
            {children}
          </Reorder.Group>
        )}
        selectAllId="select-all-banners"
        itemCount={rows.length}
        allOnPageSelected={selection.allOnPageSelected}
        someOnPageSelected={selection.someOnPageSelected}
        onToggleAllOnPage={selection.toggleAllOnPage}
        isLoading={showSkeleton}
        isEmpty={!isLoading && rows.length === 0}
        emptyTitle="Nenhum banner"
        emptyDescription="Adicione o primeiro banner da loja."
        footer={
          total > PAGE_SIZE ? (
            <Pagination
              page={pageIndex}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={setPageIndex}
              className="justify-center pt-2"
            />
          ) : null
        }
      >
        {rows.map((banner, index) => {
          const canPersist = isPersistedListId(banner._id);
          return (
          <DraggableBannerItem
            key={banner._id}
            banner={banner}
            index={index}
            selected={selection.selectedIds.has(banner._id)}
            onToggle={() => selection.toggle(banner._id)}
            onPreview={() => setPreviewUrl(resolveBannerSrc(banner.banner))}
            onEdit={() => {
              if (!canPersist) {
                toast.error('Este banner não tem id no banco. Reordene um item válido para sincronizar.');
                return;
              }
              setEditBanner(banner);
            }}
            onDelete={() => void handleDelete(banner)}
            onToggleActive={() => handleToggle(banner)}
            canPersist={canPersist}
            onDragEnd={() => persistDragEnd(banner._id)}
          />
          );
        })}
      </SelectableListLayout>

      <FloatingActionBar count={selection.count} onClear={selection.clear}>
        <Tooltip content="Exibir os banners selecionados no app" side="top">
          <Button size="sm" variant="secondary" disabled={batchBusy} onClick={() => batchSetActive(true)}>
            <Power className="size-4" /> Ativar
          </Button>
        </Tooltip>
        <Tooltip content="Ocultar os banners selecionados no app" side="top">
          <Button size="sm" variant="destructive" disabled={batchBusy} onClick={() => batchSetActive(false)}>
            <Power className="size-4" /> Desativar
          </Button>
        </Tooltip>
      </FloatingActionBar>

      <BannerCreateDialog
        idPassbook={idPassbook}
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={refresh}
      />
      <BannerEditDialog
        banner={editBanner}
        idPassbook={idPassbook}
        onClose={() => setEditBanner(null)}
        onSaved={refresh}
      />
      <Dialog open={Boolean(previewUrl)} onOpenChange={(o) => !o && setPreviewUrl(null)}>
        <DialogContent className="flex max-h-[100dvh] w-[calc(100%-0.75rem)] max-w-none flex-col gap-3 overflow-hidden p-3 sm:max-h-[calc(100dvh-2rem)] sm:max-w-4xl sm:gap-4 sm:p-6">
          <DialogHeader className="shrink-0 pr-8">
            <DialogTitle>Visualizar banner</DialogTitle>
          </DialogHeader>
          {previewUrl ? (
            <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden">
              <img
                src={previewUrl}
                alt="Banner"
                className="h-auto max-h-[calc(100dvh-6.5rem)] w-full max-w-full rounded-xl object-contain sm:max-h-[70vh]"
              />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
